/**
 * Patient Form Page
 *
 * Create or edit patient
 */

import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ArrowLeft, Save, Loader2 } from 'lucide-react'

import { usePatient, useCreatePatient, useUpdatePatient } from '@/application/patients'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Select } from '@/presentation/components/ui/Select'
import { Card, CardHeader, CardTitle, CardContent } from '@/presentation/components/ui/Card'
import { DOCUMENT_TYPE_LABELS } from '@/config/constants'
import { getErrorMessage } from '@/lib/api'
import { toast } from '@/lib/toast'

// =============================================================================
// Validation Schema
// =============================================================================

const patientSchema = z.object({
  documentType: z.enum(['CC', 'TI', 'CE', 'PA', 'RC', 'NIT', 'MS', 'AS', 'CD', 'SC', 'PE', 'NV'] as const, {
    message: 'El tipo de documento es requerido',
  }),
  documentNumber: z
    .string()
    .min(5, 'El número de documento debe tener al menos 5 caracteres')
    .max(20, 'El número de documento es muy largo')
    .regex(/^[A-Za-z0-9-]+$/, 'Solo letras, números y guiones'),
  // Colombian name format: 4 separate fields
  // Primer Nombre - requerido
  firstName: z
    .string()
    .min(2, 'El primer nombre debe tener al menos 2 caracteres')
    .max(50, 'El nombre es muy largo'),
  // Segundo Nombre - opcional
  secondName: z
    .string()
    .max(50, 'El segundo nombre es muy largo')
    .default(''),
  // Primer Apellido - requerido
  lastName: z
    .string()
    .min(2, 'El primer apellido debe tener al menos 2 caracteres')
    .max(50, 'El apellido es muy largo'),
  // Segundo Apellido - opcional
  secondLastName: z
    .string()
    .max(50, 'El segundo apellido es muy largo')
    .default(''),
  birthDate: z.string().min(1, 'La fecha de nacimiento es requerida'),
  sex: z.enum(['M', 'F', 'O'] as const, {
    message: 'El sexo es requerido',
  }),
  phone: z.string().max(15, 'El teléfono es muy largo').optional().or(z.literal('')),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  address: z.string().max(255, 'La dirección es muy larga').optional().or(z.literal('')),
  city: z.string().max(100, 'La ciudad es muy larga').optional().or(z.literal('')),
})

type PatientFormData = z.input<typeof patientSchema>

// =============================================================================
// Constants
// =============================================================================

const DOCUMENT_TYPE_OPTIONS = Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}))

const SEX_OPTIONS = [
  { value: 'M', label: 'Masculino' },
  { value: 'F', label: 'Femenino' },
  { value: 'O', label: 'Otro' },
]

// =============================================================================
// Component
// =============================================================================

export function PatientFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditing = !!id

  // Queries
  const { data: patient, isLoading: isLoadingPatient } = usePatient(id)
  const createMutation = useCreatePatient()
  const updateMutation = useUpdatePatient()

  const isSubmitting = createMutation.isPending || updateMutation.isPending

  // Form
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PatientFormData>({
    resolver: zodResolver(patientSchema),
    defaultValues: {
      documentType: 'CC',
      secondName: '',
      secondLastName: '',
      sex: 'M',
    },
  })

  // Load patient data when editing
  useEffect(() => {
    if (patient) {
      reset({
        documentType: patient.document.type as PatientFormData['documentType'],
        documentNumber: patient.document.number,
        firstName: patient.firstName,
        secondName: patient.secondName || '',
        lastName: patient.lastName,
        secondLastName: patient.secondLastName || '',
        birthDate: patient.birthDate.split('T')[0], // Convert to YYYY-MM-DD
        sex: patient.sex,
        phone: patient.contact?.phone || '',
        email: patient.contact?.email || '',
        address: patient.contact?.address || '',
        city: patient.contact?.city || '',
      })
    }
  }, [patient, reset])

  // Handlers
  const onSubmit = async (data: PatientFormData) => {
    try {
      // Transform to backend format
      const payload = {
        document: {
          type: data.documentType,
          number: data.documentNumber.trim(),
        },
        firstName: data.firstName.trim(),
        secondName: data.secondName?.trim() || undefined,
        lastName: data.lastName.trim(),
        secondLastName: data.secondLastName?.trim() || undefined,
        // La fecha de nacimiento es un dia del calendario, no un instante.
        // Convertirla con toISOString() la volvia medianoche UTC y al
        // mostrarla en Colombia retrocedia un dia: el paciente aparecia
        // nacido el 21 cuando la recepcion escribio 22, y con esa fecha
        // corrida no podia entrar a su portal.
        birthDate: data.birthDate,
        sex: data.sex,
        contact: {
          phone: data.phone || undefined,
          email: data.email || undefined,
          address: data.address || undefined,
          city: data.city || undefined,
          country: 'Colombia',
        },
      }

      if (isEditing) {
        await updateMutation.mutateAsync({
          id: id!,
          data: {
            firstName: payload.firstName,
            secondName: data.secondName?.trim() || null, // null = clear field
            lastName: payload.lastName,
            secondLastName: data.secondLastName?.trim() || null, // null = clear field
            birthDate: payload.birthDate,
            sex: payload.sex,
            contact: payload.contact,
          },
        })
        toast.success('Paciente actualizado')
      } else {
        await createMutation.mutateAsync(payload)
        toast.success('Paciente creado')
      }
      navigate('/patients')
    } catch (err) {
      toast.error(getErrorMessage(err))
    }
  }

  const mutationError = createMutation.error || updateMutation.error

  if (isEditing && isLoadingPatient) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-[var(--foreground)]">
            {isEditing ? 'Editar Paciente' : 'Nuevo Paciente'}
          </h1>
          <p className="text-[var(--foreground-muted)]">
            {isEditing
              ? 'Actualiza la información del paciente'
              : 'Ingresa los datos del nuevo paciente'}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <CardHeader>
            <CardTitle>Información Personal</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Error Alert */}
            {mutationError && (
              <div className="p-3 rounded-lg bg-[var(--destructive)]/10 border border-[var(--destructive)]/20 text-[var(--destructive)] text-sm">
                {getErrorMessage(mutationError)}
              </div>
            )}

            {/* Document */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                {...register('documentType')}
                label="Tipo de Documento"
                options={DOCUMENT_TYPE_OPTIONS}
                error={errors.documentType?.message}
                disabled={isEditing}
              />
              <Input
                {...register('documentNumber')}
                label="Número de Documento"
                placeholder="Ej: 1234567890"
                error={errors.documentNumber?.message}
                disabled={isEditing}
              />
            </div>

            {/* Names - Colombian format: 4 fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Input
                {...register('firstName')}
                label="Primer Nombre *"
                placeholder="Ej: Juan"
                error={errors.firstName?.message}
              />
              <Input
                {...register('secondName')}
                label="Segundo Nombre"
                placeholder="Ej: Carlos"
                error={errors.secondName?.message}
              />
              <Input
                {...register('lastName')}
                label="Primer Apellido *"
                placeholder="Ej: Pérez"
                error={errors.lastName?.message}
              />
              <Input
                {...register('secondLastName')}
                label="Segundo Apellido"
                placeholder="Ej: García"
                error={errors.secondLastName?.message}
              />
            </div>

            {/* Birth & Sex */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                {...register('birthDate')}
                type="date"
                label="Fecha de Nacimiento"
                error={errors.birthDate?.message}
              />
              <Select
                {...register('sex')}
                label="Sexo"
                options={SEX_OPTIONS}
                error={errors.sex?.message}
              />
            </div>

            {/* Contact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                {...register('phone')}
                label="Teléfono"
                placeholder="Ej: 3001234567"
                error={errors.phone?.message}
              />
              <Input
                {...register('email')}
                type="email"
                label="Email"
                placeholder="Ej: paciente@email.com"
                error={errors.email?.message}
              />
            </div>

            {/* Address */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                {...register('address')}
                label="Dirección"
                placeholder="Ej: Calle 123 #45-67"
                error={errors.address?.message}
              />
              <Input
                {...register('city')}
                label="Ciudad"
                placeholder="Ej: Bogotá"
                error={errors.city?.message}
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-4 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {isEditing ? 'Actualizar' : 'Crear Paciente'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  )
}
